import { computed } from "vue";
import type { Component, ComputedRef } from "vue";
import type { RouteLocationRaw, RouteRecordRaw } from "vue-router";
import { SectionList } from "./ViewModule/sectionList";

export enum ServiceTypes {
  All = 0,
  AgentClient = 1,
  Server = 2,
  WebAdmin = 3,
  WebApp = 4,
}

export function getServiceTypeString(serviceType: ServiceTypes): string {
  const index = Object.values(ServiceTypes).indexOf(serviceType);
  return Object.keys(ServiceTypes)[index];
}

type IModuleRegisterParams = {};

// define a interface for modules
interface IModule {
  register(params: IModuleRegisterParams): void | Promise<void>;
  name: string;
  version: string;
  description: string;
}

class ViewModule {
  constructor(
    section: SectionList | string,
    key: string,
    ViewComponent: Component,
    order: number | undefined,
    // props can be used to pass data to the component
    // cannot be string or number
    Condition: (rtcId: string) => boolean | undefined,
    props: Record<string, any> = {},
    stackable: boolean | undefined,
  ) {
    this.section = section;
    this.ViewComponent = ViewComponent;
    this.key = key;
    this.order = order || 0;
    this.Condition = Condition;
    this.props = props;
    this.stackable = stackable ?? true;
  }
  key: string;
  section: SectionList | string;
  order: number = 0;
  ViewComponent: Component;
  Condition: (rtcId: string) => boolean | undefined;
  props: Record<string, any>;
  size: number | undefined;
  tabTitle: string | undefined;
  stackable: boolean = true;
}

type HookModuleConstructorParams<T, TLocation> = {
  name: string;
  location: TLocation;
  description: string;
  priority: number;
  critical: boolean;
  run: HookModuleRunnerMethod<T>;
  condition?: (rtcId: string) => boolean | undefined;
};

type HookModuleRunnerMethod<T> = (state: T) => T;

class HookModule<T, TLocation> {
  name: string;
  description: string;
  priotiy: number;
  critical: boolean;
  location: TLocation;
  condition: (rtcId: string) => boolean | undefined;
  private runner: HookModuleRunnerMethod<T>;

  public run(state: T): T {
    try {
      return this.runner(state);
    } catch (ex) {
      console.error("Failed to run hook module: " + this.name);
      console.error(ex);
      if (this.critical) {
        throw ex;
      }
      return state;
    }
  }

  constructor({
    name,
    location,
    description,
    priority = 0,
    critical = false,
    run,
    condition,
  }: HookModuleConstructorParams<T, TLocation>) {
    if (name === undefined) throw new Error("name is undefined");
    if (location === undefined) throw new Error("location is undefined");
    if (run === undefined) throw new Error("run is undefined");

    this.name = name;
    this.location = location;
    this.description = description;
    this.priotiy = priority;
    this.critical = critical;
    this.runner = run;
    this.condition = condition || ((_) => true);
  }
}

class IModuleImport {
  constructor(instance: IModule) {
    this.default = instance;
  }
  default: IModule;
}

class ISideBarMenuItem {
  public title: string;
  public icon: string;
  public to: string | RouteLocationRaw;
  public role: string | undefined;
  public children: ISideBarMenuItem[] | undefined;
  public parent: string | undefined;
  public before: string | undefined;
  public after: string | undefined;
}

class ModuleManager {
  private static modules: IModule[] = [];
  private static viewModules: ViewModule[] = [];
  private static hookModules: HookModule<any, any>[] = [];
  public static routes: RouteRecordRaw[] = [];
  public static locales: Record<string, any>[] = [];
  public static i18nNotifiers: ((translations: Record<string, any>, source: string | undefined) => void)[] = [];
  public static sideBarMenuItems: ISideBarMenuItem[] = [];

  static async registerModules(module: Promise<IModuleImport>[]) {
    for (const m of module) {
      await this.registerModule(m);
    }
    console.log(`Registered ${this.modules.length} modules. List: ${this.modules.map((x) => x.name).join(", ")} `);
    console.log(
      `Registered ${this.viewModules.length} view modules. List: ${this.viewModules
        .map((x) => `${x.key} -> ${x.section}`)
        .join(", ")} `,
    );
    console.log(`Registered ${this.hookModules.length} hook modules. List: ${this.hookModules.map((x) => x.name).join(", ")} `);
    console.log(`Registered ${this.routes.length} routes. List: ${this.routes.map((x) => x.name).join(", ")}`);
  }

  static async registerModule(modulePromise: Promise<IModuleImport>) {
    const module = (await modulePromise).default;

    try {
      const returnVal = module.register({ moduleManager: this });
      if (returnVal instanceof Promise) {
        await returnVal;
      }
      this.modules.push(module);
    } catch (ex) {
      console.error("Failed to initialize module: " + module.name);
      console.error(ex);
    }
  }

  static addRoute(route: RouteRecordRaw) {
    this.routes.push(route);
  }

  static addViewModule(module: ViewModule) {
    this.viewModules.push(module);
  }

  static getViewModules(section: SectionList, rtcId: string): ComputedRef<ViewModule[]> {
    return computed(() => {
      return this.viewModules
        .filter((x) => x.section === section)
        .filter((x) => x.Condition === undefined || x.Condition(rtcId))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    });
  }

  static getModules() {
    return this.modules;
  }

  static getHookModules<T, TLocation>(location: TLocation, rtcId: string = "root"): HookModule<T, TLocation>[] {
    // this gets the modules that are of type HookModule<T, TLocation> and have the location of TLocation

    const filtered1 = this.hookModules.filter((x) => {
      console.log(typeof x);
      return true;
    });

    const modules = filtered1
      .filter((x) => x.location === location)
      .filter((x) => x.condition === undefined || x.condition(rtcId)) as HookModule<T, TLocation>[];

    // sort the modules by priority
    modules.sort((a, b) => a.priotiy - b.priotiy);

    return modules;
  }

  static addHookModule(module: HookModule<any, any>) {
    this.hookModules.push(module);
  }

  static addLocales(locales: Record<string, any>, source: string | undefined) {
    this.locales.push(locales);
    // notify all i18n instances to reload
    this.i18nNotifiers.forEach((x) => x(locales, source));
  }

  static addSideBarMenuItem(menuItem: ISideBarMenuItem) {
    this.sideBarMenuItems.push(menuItem);
  }

  static injectSideBarMenuItems(menuItems: ISideBarMenuItem[], root: boolean = true) {
    // this is used to inject menu items from other modules into the sidebar
    // modify the menuItems array to add or remove items
    // this is called when the sidebar is being rendered

    for (const existingMenu of menuItems) {
      if (existingMenu.children !== undefined) {
        existingMenu.children = this.injectSideBarMenuItems(existingMenu.children, false);
      }

      const matchingChilds = this.sideBarMenuItems.filter((x) => x.parent === existingMenu.title);

      if (matchingChilds !== undefined) {
        if (existingMenu.children === undefined) {
          existingMenu.children = [];
        }
        this.addChildren(existingMenu.children, matchingChilds);
      }
    }
    if (root) {
      const matchingRoots = this.sideBarMenuItems.filter((x) => x.parent === undefined);
      if (matchingRoots !== undefined) {
        this.addChildren(menuItems, matchingRoots);
      }
    }
    // return the modified menuItems
    return menuItems;
  }

  static addChildren(existingMenu: ISideBarMenuItem[], matchingChilds: ISideBarMenuItem[]) {
    for (const child of matchingChilds) {
      if (child.children === undefined) {
        child.children = [];
      }
      this.injectSideBarMenuItems([child], false);

      if (child.before !== undefined) {
        const index = existingMenu.findIndex((x) => x.title === child.before);
        if (index !== -1) {
          existingMenu.splice(index, 0, child);
          continue;
        }
      } else if (child.after !== undefined) {
        const index = existingMenu.findIndex((x) => x.title === child.after);
        if (index !== -1) {
          existingMenu.splice(index + 1, 0, child);
          continue;
        }
      } else {
        existingMenu.push(child);
      }
    }
  }
}

export { HookModule, ViewModule, ModuleManager, SectionList };
export type { IModule, IModuleRegisterParams, ISideBarMenuItem };
